import { useCallback, useContext, useRef } from 'react';
import type { RefObject } from 'react';
import type { SiteHeaderContextType } from '../../unlisted/SiteHeaderContext';
import { SiteHeaderContext } from '../../unlisted/SiteHeaderContext';
import type { VideoRef } from '../../atoms/video/Video.data';
import gsap, { Quad } from 'gsap';
import useMount from '../../../hooks/useMount';
import useWindowSize from '../../../hooks/useWindowSize';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import useToggle from '../../../hooks/useToggle';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import {
  enterFullScreenTransition,
  exitFullScreenTransition,
} from './HeaderShowrealLarge.transitions';
import { useGlobalStore } from '../../../stores/global';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import type { HeaderShowrealLargeBlokData } from './HeaderShowrealLarge.data';
import useScrollTrigger from '../../../hooks/useScrollTrigger';

export type FullScreenRefs = {
  maskTopTitleRef: RefObject<HTMLHeadingElement | null>;
  maskBottomTitleRef: RefObject<HTMLHeadingElement | null>;
  topTitleRef: RefObject<HTMLHeadingElement | null>;
  bottomTitleRef: RefObject<HTMLHeadingElement | null>;
  maskedContentRef: RefObject<HTMLDivElement | null>;
  previewContainerRef: RefObject<HTMLDivElement | null>;
  videoContainerRef: RefObject<HTMLDivElement | null>;
  videoRef: RefObject<VideoRef | null>;
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  onPlayClick: () => void;
};

export function useFullScreen(
  blok: HeaderShowrealLargeBlokData,
  elementRef: RefObject<HTMLDivElement | null>,
): FullScreenRefs {
  const topTitleRef = useRef<HTMLHeadingElement>(null);
  const bottomTitleRef = useRef<HTMLHeadingElement>(null);
  const maskTopTitleRef = useRef<HTMLHeadingElement>(null);
  const maskBottomTitleRef = useRef<HTMLHeadingElement>(null);
  const maskedContentRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<VideoRef>(null);
  const { siteHeaderElementRef } = useContext(SiteHeaderContext) as SiteHeaderContextType;
  const [isFullScreen, toggleFullScreen] = useToggle(false);
  const { width, height } = useWindowSize();

  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;

  const enterFullScreenTimeline = useSetupTimeline(enterFullScreenTransition, {
    topTitleRef,
    bottomTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    siteHeaderElementRef,
  });
  const exitFullScreenTimeline = useSetupTimeline(exitFullScreenTransition, {
    topTitleRef,
    bottomTitleRef,
    maskTopTitleRef,
    maskBottomTitleRef,
    siteHeaderElementRef,
  });

  function setClipPath(clipPath: { [key: string]: string }, animate: boolean = false): void {
    if (animate) {
      gsap.to(maskedContentRef.current, {
        ...clipPath,
        duration: 0.4,
        ease: 'Quad.easeInOut',
      });
    } else {
      gsap.set(maskedContentRef.current, clipPath);
    }
  }

  const getVideoHeight = useCallback(
    (isFullScreen: boolean = false) => (isFullScreen ? window.innerHeight : '100%'),
    [],
  );

  function setVideoContainerHeight(height: string | number, animate: boolean = false): void {
    if (animate) {
      gsap.to(videoContainerRef.current, {
        height,
        duration: 0.4,
        ease: 'Quad.easeInOut',
        onUpdate: () => {
          videoRef?.current?.updateVideoSize();
        },
      });
    } else {
      gsap.set(videoContainerRef.current, {
        height,
      });
      videoRef?.current?.updateVideoSize();
    }
  }

  const getClipPath = useCallback(
    (isFullScreen: boolean = false) => {
      const previewBounds = previewContainerRef?.current?.getBoundingClientRect();
      const contentBounds = maskedContentRef?.current?.getBoundingClientRect();
      let bottom = 0;
      let xOffset = 0;
      let top = 0;

      if (previewBounds && contentBounds) {
        top = ((previewBounds.top - contentBounds.top) / contentBounds.height) * 100;
        bottom = 0;
        xOffset = 0;

        if (isFullScreen) {
          bottom =
            100 -
            ((contentBounds.bottom - (previewBounds.top + window.innerHeight)) /
              contentBounds.height) *
              100;
          xOffset = 0;
        } else {
          bottom =
            100 - ((contentBounds.bottom - previewBounds.bottom) / contentBounds.height) * 100;
          xOffset = ((previewBounds.left - contentBounds.left) / contentBounds.width) * 100;
        }
      }

      return {
        clipPath: `polygon(
          ${xOffset}% ${top}%, 
          ${100 - xOffset}% ${top}%,
          ${100 - xOffset}% ${bottom}%,
          ${xOffset}% ${bottom}%
        )`,
      };
    },
    [maskedContentRef, previewContainerRef],
  );

  useMount(() => {
    setClipPath(getClipPath(isFullScreen));
  });

  const scrollToTop = (callback: () => void): void => {
    let currentY = scrollContext.scrollStatusRef.current?.offset.y;
    if (typeof currentY !== 'number') {
      currentY = 0;
    }
    const y = currentY + (previewContainerRef.current?.getBoundingClientRect().top as number);

    if (y === currentY) {
      callback();
    } else {
      scrollContext.scrollTo(0, y, 400, {
        easing: Quad.easeInOut,
        callback: () => {
          callback();
        },
      });
    }
  };

  const fullScreenCompleted = useRef<boolean>(false);

  // When we change full-screen mode
  useUpdateEffect(() => {
    if (isFullScreen) {
      setClipPath(getClipPath(isFullScreen), true);
      setVideoContainerHeight(getVideoHeight(isFullScreen), true);
      scrollToTop(() => {
        // scrollContext.lock()
        exitFullScreenTimeline.kill();
        enterFullScreenTimeline.restart();
        fullScreenCompleted.current = isFullScreen;
      });
    } else {
      fullScreenCompleted.current = isFullScreen;
      enterFullScreenTimeline.kill();
      exitFullScreenTimeline.restart();
      // scrollContext.unLock();
      setClipPath(getClipPath(isFullScreen), true);
      setVideoContainerHeight(getVideoHeight(isFullScreen), true);
    }
  }, [isFullScreen]);

  // When the window resizes
  useUpdateEffect(() => {
    // scrollToTop ?
    setVideoContainerHeight(getVideoHeight(isFullScreen), false);
    setClipPath(getClipPath(isFullScreen), false);
  }, [width, height]);

  const store = useGlobalStore();
  const isDesktopDevice = useIsDesktopDevice();
  const onPlayClick = useCallback(() => {
    if (isDesktopDevice) {
      toggleFullScreen();
    } else {
      store.openVideoModal({
        video: blok.video[0],
      });
    }
  }, [isDesktopDevice, toggleFullScreen, store, blok.video]);

  useScrollTrigger({
    trigger: elementRef,
    vars: {
      start: 'top bottom',
      end: 'bottom top',
      onUpdate() {
        if (fullScreenCompleted.current) {
          toggleFullScreen();
        }
      },
    },
  });

  return {
    isFullScreen,
    onPlayClick,
    toggleFullScreen,
    maskTopTitleRef,
    maskBottomTitleRef,
    topTitleRef,
    bottomTitleRef,
    maskedContentRef,
    previewContainerRef,
    videoContainerRef,
    videoRef,
  };
}
